import { store } from '@/app/store';
import { selectUserInfo } from '@/features/chats/model/recentChat/selectors';
import { updateInviteStatus } from '@/features/invite/model/inviteSlice';
import { getInviteStatus } from '@/infrastructure/sdk/invite';
import { formatUserIdToJid, getUserIdFromJid, logger } from '@/shared/utils';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const selectGeneratedInviteLink = state => state.inviteData?.generatedLink || null;
export const selectRequestUsers = state => state.inviteData?.requestUsers || EMPTY_ARRAY;
export const selectRequestUsersLoading = state => state.inviteData?.requestUsersLoading || false;
export const selectRequestUsersClearing = state => state.inviteData?.requestUsersClearing || false;
export const selectRequestUsersHasMore = state => state.inviteData?.requestUsersHasMore || false;
export const selectInviteUserCount = state => state.inviteData?.inviteUserCount || 0;
export const selectInviteStatusForUser = userId => state => {
   return state.inviteData?.inviteStatusByUser?.[userId]?.inviteStatus ?? null;
};
export const selectInviteInfoForUser = userId => state => {
   return state.inviteData?.inviteStatusByUser?.[userId] || EMPTY_OBJECT;
};

export const resolveInviteInfo = async userId => {
   const state = store.getState();
   const info = selectInviteInfoForUser(userId)(state);
   if (info !== EMPTY_OBJECT && info.isSender !== undefined) return info;
   if (selectRequestUsers(state).some(u => u.fromUserId === userId)) return { inviteStatus: 1, isSender: false };
   try {
      const res = await getInviteStatus(formatUserIdToJid(userId));
      if (res?.statusCode === 200) {
         const { inviteStatus, remainingMessages, isSender } = res.data;
         store.dispatch(updateInviteStatus({ userId, inviteStatus, remainingMessages, isSender }));
         return { inviteStatus, remainingMessages, isSender };
      }
   } catch (err) {
      logger.error('[resolveInviteInfo]:', err);
   }
   return EMPTY_OBJECT;
};

export const selectSearchTextByScope = scope => state => state.inviteData?.searchTextByScope?.[scope] || '';

export const selectIsKnownRequest = (state, userId) =>
   selectInviteInfoForUser(userId)(state).inviteStatus === 1 ||
   selectRequestUsers(state).some(u => u.fromUserId === userId);

export const selectInvitePermissions = createSelector(
   [(state, jid) => selectInviteInfoForUser(getUserIdFromJid(jid))(state) || EMPTY_OBJECT, (_, jid) => jid],
   (inviteInfo, jid) => {
      const inviteStatus = inviteInfo.inviteStatus;
      const senderFlag = inviteInfo.isSender;
      const isGroup = jid.includes('@mix');

      const isAccepted = isGroup || inviteStatus === 2;
      const isRequested = !isGroup && inviteStatus === 1;
      const isSender = isRequested && !!senderFlag;
      const isReceiver = isRequested && !isSender;

      return {
         inviteStatus,
         isReceiver,
         canChat: isAccepted || inviteStatus === 0 || isSender,
         isFeatureGated: !isAccepted,
         remainingMessages: inviteInfo.remainingMessages ?? 0,
      };
   },
);

export const selectFilteredRequestUsers = scope =>
   createSelector(
      [selectRequestUsers, selectSearchTextByScope(scope), selectUserInfo],
      (requestUsers, searchText, userInfo) => {
         if (!searchText) return requestUsers;
         const lowerSearchText = searchText.toLowerCase();
         return requestUsers.filter(user => {
            const userId = user.userId || user.fromUserId || '';
            const userProfile = userInfo[userId] || {};
            const nickName = userProfile.nickName || user.messageBody?.nickName || user.nickName || '';

            const lowerNickName = (nickName || '').toLowerCase();
            const lowerUserId = (userId || '').toLowerCase();

            return lowerNickName.includes(lowerSearchText) || lowerUserId.includes(lowerSearchText);
         });
      },
   );

export const useInviteStatus = userId => useSelector(selectInviteStatusForUser(userId));
